import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { Filter, SlidersHorizontal } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ProductsPage = () => {
  const { type } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPurity, setSelectedPurity] = useState("all");
  const [sortBy, setSortBy] = useState("price_low");

  useEffect(() => {
    fetchProducts();
  }, [type]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const url = type ? `${API}/products?metal_type=${type}` : `${API}/products`;
      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products
    .filter(p => selectedPurity === "all" || p.purity === selectedPurity)
    .sort((a, b) => {
      if (sortBy === "price_low") return a.total_price - b.total_price;
      if (sortBy === "price_high") return b.total_price - a.total_price;
      if (sortBy === "weight_low") {
        const wA = a.weight_unit === "g" ? a.weight : a.weight / 1000;
        const wB = b.weight_unit === "g" ? b.weight : b.weight / 1000;
        return wA - wB;
      }
      if (sortBy === "weight_high") {
        const wA = a.weight_unit === "g" ? a.weight : a.weight / 1000;
        const wB = b.weight_unit === "g" ? b.weight : b.weight / 1000;
        return wB - wA;
      }
      return 0;
    });

  const purities = [...new Set(products.map(p => p.purity))];
  const pageTitle = type === "gold" ? "Gold Lakshmi Coins" : type === "silver" ? "Silver Lakshmi Coins" : "All Products";
  const accentColor = type === "silver" ? "#C0C0C0" : "#D4AF37";

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="products-page">
        {/* Header */}
        <div className="mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] mb-2" style={{ color: accentColor }}>
            {type === "gold" ? "24K Pure Gold (999.9)" : type === "silver" ? "999 Fine Silver" : "Premium Bullion"}
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white" data-testid="page-title">
            {pageTitle}
          </h1>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8 pb-8 border-b border-[rgba(255,255,255,0.05)]" data-testid="filters-section">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#737373]" />
            <span className="text-sm text-[#737373]">Filter:</span>
          </div>
          
          <Select value={selectedPurity} onValueChange={setSelectedPurity}>
            <SelectTrigger className="w-[140px] bg-[#121212] border-[rgba(255,255,255,0.1)]" data-testid="purity-filter">
              <SelectValue placeholder="Purity" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
              <SelectItem value="all">All Purities</SelectItem>
              {purities.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 ml-auto">
            <SlidersHorizontal className="w-4 h-4 text-[#737373]" />
            <span className="text-sm text-[#737373]">Sort:</span>
          </div>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px] bg-[#121212] border-[rgba(255,255,255,0.1)]" data-testid="sort-filter">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
              <SelectItem value="price_low">Price: Low to High</SelectItem>
              <SelectItem value="price_high">Price: High to Low</SelectItem>
              <SelectItem value="weight_low">Weight: Low to High</SelectItem>
              <SelectItem value="weight_high">Weight: High to Low</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-[#121212] h-48 mb-4" />
                <div className="bg-[#121212] h-4 w-3/4 mb-2" />
                <div className="bg-[#121212] h-6 w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <p className="text-xl text-[#737373]">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="products-grid">
            {filteredProducts.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}>
                  <div className={`product-card ${product.metal_type}`}>
                    <div className="relative overflow-hidden">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="product-image"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`badge-${product.metal_type}`}>
                          {product.purity}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-[#737373] uppercase tracking-wider mb-1">
                        {product.weight}{product.weight_unit} {product.metal_type}
                      </p>
                      <h3 className="text-white font-medium mb-3 line-clamp-2 min-h-[48px]">
                        {product.name}
                      </h3>
                      
                      {/* Pricing Breakdown */}
                      <div className="space-y-1 mb-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#737373]">Metal Value</span>
                          <span className="text-[#A3A3A3]">₹{product.live_price?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#737373]">Making + GST</span>
                          <span className="text-[#A3A3A3]">₹{product.making_charges_gst?.toLocaleString()}</span>
                        </div>
                      </div>
                      
                      <div className="pt-3 border-t border-[rgba(255,255,255,0.05)]">
                        <p className={`price-tag ${product.metal_type} text-2xl font-medium`}>
                          ₹{product.total_price?.toLocaleString()}
                        </p>
                        <p className="text-xs text-[#737373]">Total Price</p>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Results Count */}
        {!loading && (
          <p className="text-sm text-[#737373] mt-8" data-testid="results-count">
            Showing {filteredProducts.length} of {products.length} products
          </p>
        )}
      </div>
    </Layout>
  );
};

export default ProductsPage;
